import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '@/lib/analyticsService';

interface SEOProps {
  title?: string;
  description?: string;
  imageUrl?: string;
  url?: string;
}

export const SEO = ({ title, description, imageUrl, url }: SEOProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Track page view when component mounts or location changes
  React.useEffect(() => {
    trackPageView(location.pathname, title);
  }, [location.pathname, title]);
  
  const defaultTitle = t('seo.defaultTitle');
  const defaultDescription = t('seo.defaultDescription');
  const siteName = 'INS Organization';
  const baseUrl = 'https://www.insorganizasyon.org';
  const defaultImageUrl = `${baseUrl}/images/hero/hero-poster.jpg`;

  const seo = {
    title: title ? `${title} | ${siteName}` : defaultTitle,
    description: description || defaultDescription,
    image: imageUrl || defaultImageUrl,
    url: url ? `${baseUrl}${url}` : `${baseUrl}${location.pathname === '/' ? '' : location.pathname}`,
    lang: i18n.language,
  };

  // Map language codes to region-specific Open Graph locale codes
  const localeMap = { en: 'en_US', tr: 'tr_TR', fr: 'fr_FR', ar: 'ar_SA', ru: 'ru_RU' } as const;
  const locales = Object.keys(localeMap);
  const currentOgLocale = localeMap[(i18n.language as keyof typeof localeMap)] || 'en_US';

  // Helper to build hreflang alternate URLs using querystring lang
  const getAlternateUrl = (lang: string) => {
    try {
      const u = new URL(seo.url);
      u.searchParams.set('lang', lang);
      return u.toString();
    } catch {
      return `${seo.url}${seo.url.includes('?') ? '&' : '?'}lang=${lang}`;
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'NGO',
    name: siteName,
    url: baseUrl,
    logo: `${baseUrl}/images/logo/INS_1_192.png`,
    sameAs: [
      'https://x.com/insorganizasyon',
      'https://www.instagram.com/insorganizasyon',
      'https://www.linkedin.com/in/insorganizasyon',
      'https://medium.com/@insorganization.org',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+90-555-123-4567',
      contactType: 'Customer Service',
    },
  };

  return (
    <Helmet
      htmlAttributes={{
        lang: seo.lang,
        dir: i18n.dir(),
      }}
      title={seo.title}
      meta={[
        { name: 'description', content: seo.description },
        // Open Graph tags
        { property: 'og:url', content: seo.url },
        { property: 'og:title', content: seo.title },
        { property: 'og:description', content: seo.description },
        { property: 'og:image', content: seo.image },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:alt', content: siteName },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: siteName },
        { property: 'og:locale', content: currentOgLocale },
        // Open Graph alternate locales
        ...locales
          .filter(l => l !== i18n.language)
          .map(l => ({ property: 'og:locale:alternate', content: localeMap[l as keyof typeof localeMap] })),
        // Twitter Card tags
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: seo.title },
        { name: 'twitter:description', content: seo.description },
        { name: 'twitter:image', content: seo.image },
        { name: 'twitter:site', content: '@insorganizasyon' },
        // UI meta
        { name: 'theme-color', content: '#0F766E' },
      ]}
    >
        <link rel="canonical" href={seo.url} />
        {/* Resource hints for third-party scripts/assets */}
        <link rel="preconnect" href="https://plausible.io" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        <link rel="dns-prefetch" href="https://medium.com" />
        <link rel="dns-prefetch" href="https://x.com" />

        {/* Preload local fonts */}
        <link rel="preload" href="/fonts/Inter-Regular.ttf" as="font" type="font/ttf" crossOrigin="anonymous" />
        <link rel="preload" href="/fonts/Inter-Bold.ttf" as="font" type="font/ttf" crossOrigin="anonymous" />

        {/* Preload hero image on home for faster LCP */}
        {location.pathname === '/' ? (
          <link rel="preload" href={defaultImageUrl} as="image" />
        ) : null}

        {/* Hreflang alternates mapped to querystring-based language */}
        <link rel="alternate" hrefLang="x-default" href={seo.url} />
        <link rel="alternate" hrefLang="en" href={getAlternateUrl('en')} />
        <link rel="alternate" hrefLang="tr" href={getAlternateUrl('tr')} />
        <link rel="alternate" hrefLang="fr" href={getAlternateUrl('fr')} />
        <link rel="alternate" hrefLang="ar" href={getAlternateUrl('ar')} />
        <link rel="alternate" hrefLang="ru" href={getAlternateUrl('ru')} />

        <script type="application/ld+json">
            {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              url: baseUrl,
              name: siteName,
              potentialAction: {
                '@type': 'SearchAction',
                target: `${baseUrl}/?q={search_term_string}`,
                'query-input': 'required name=search_term_string'
              }
            })}
        </script>
    </Helmet>
  );
};
